/**
 * Generic paginated Excel export utility.
 *
 * Uses xlsx-js-style (drop-in SheetJS fork) for full cell-level styling.
 *
 * Designed for 10,000–50,000 row exports without freezing the browser:
 *   - Fetches data page-by-page sequentially
 *   - Appends rows incrementally (no full-dataset accumulation)
 *   - Yields control to the browser after each page
 *   - Retries failed requests with exponential back-off
 *   - Supports AbortController cancellation
 *   - Dynamically calculates column widths in the same pass as row writing
 */

import XLSXStyle from 'xlsx-js-style';

// ─── Brand palette (Altcase primary: #21B546 green) ──────────────────────────
const BRAND = {
  headerBg:     '21B546',  // Altcase primary green
  headerFg:     'FFFFFF',  // White text on header
  bandEvenBg:   'FFFFFF',  // Clean white rows
  bandOddBg:    'EDFBF2',  // primary-light tint (#D4F5DD → lightened for readability)
  borderColor:  'C2EDD0',  // Soft green border matching primary-light
  dateFg:       '158530',  // primary-active (darker green) for date cells
  textNormal:   '111827',  // Gray-900 body text
  textMuted:    '6B7280',  // Gray-500 secondary text
};

// ─── Shared border definition (all four sides) ───────────────────────────────
const border = (color = BRAND.borderColor) => ({
  top:    { style: 'thin', color: { rgb: color } },
  bottom: { style: 'thin', color: { rgb: color } },
  left:   { style: 'thin', color: { rgb: color } },
  right:  { style: 'thin', color: { rgb: color } },
});

// ─── Cell style factories ─────────────────────────────────────────────────────
const headerCellStyle = () => ({
  font:      { bold: true, sz: 11, color: { rgb: BRAND.headerFg }, name: 'Calibri' },
  fill:      { patternType: 'solid', fgColor: { rgb: BRAND.headerBg } },
  alignment: { horizontal: 'center', vertical: 'center', wrapText: false },
  border:    border(BRAND.headerBg),
});

const dataCellStyle = (rowIndex, align = 'left') => {
  const isOdd = rowIndex % 2 !== 0;
  return {
    font:      { sz: 10, color: { rgb: BRAND.textNormal }, name: 'Calibri' },
    fill:      { patternType: 'solid', fgColor: { rgb: isOdd ? BRAND.bandOddBg : BRAND.bandEvenBg } },
    alignment: { horizontal: align, vertical: 'center', wrapText: false },
    border:    border(),
  };
};

const numberCellStyle = (rowIndex) => ({
  ...dataCellStyle(rowIndex, 'right'),
  numFmt: '#,##0.00',
});

const dateDateStyle = (rowIndex) => ({
  ...dataCellStyle(rowIndex, 'center'),
  font: { sz: 10, color: { rgb: BRAND.dateFg }, name: 'Calibri', bold: false },
});

// ─── Helpers ──────────────────────────────────────────────────────────────────

const yieldToBrowser = () => new Promise((resolve) => setTimeout(resolve, 0));
const backoff = (attempt) => new Promise((resolve) => setTimeout(resolve, 500 * (attempt + 1)));

const COL_WIDTH_MAX = 52;
const COL_WIDTH_MIN = 8;

const cellLen = (v) => (v == null || v === '' ? 0 : String(v).length);

// ─── Core export engine ───────────────────────────────────────────────────────

/**
 * @typedef {object} ExportColumn
 * @property {string}  header        - Column header label
 * @property {string}  key           - Key in object returned by mapRow()
 * @property {'text'|'number'|'date'} [type] - Controls cell style (default 'text')
 *
 * @param {object}      config
 * @param {string}      config.filename
 * @param {string}      [config.sheetName]
 * @param {ExportColumn[]} config.columns
 * @param {Function}    config.fetchPage        - async (filterParams, page, pageSize) => { records, total }
 * @param {Function}    config.mapRow           - (record) => plain object
 * @param {object}      [config.filterParams]
 * @param {number}      [config.pageSize=100]
 * @param {number}      [config.maxRetries=3]
 * @param {Function}    [config.onProgress]
 * @param {AbortSignal} [config.signal]
 * @returns {Promise<object>}  metrics
 */
export async function runExcelExport(config) {
  const {
    filename,
    sheetName = 'Export',
    columns,
    fetchPage,
    mapRow,
    filterParams = {},
    pageSize = 100,
    maxRetries = 3,
    onProgress,
    signal,
  } = config;

  const metrics = {
    totalExported: 0,
    totalPages: 0,
    totalCalls: 0,
    failedRequests: 0,
    retriedRequests: 0,
    startTime: Date.now(),
    duration: 0,
  };

  const emit = (phase, partial = {}) =>
    onProgress?.({ phase, currentPage: 0, totalPages: 0, totalRecords: 0, exportedCount: 0, percentage: 0, status: '', ...partial, metrics: { ...metrics } });

  const checkCancelled = () => {
    if (signal?.aborted) throw new DOMException('Export cancelled by user', 'AbortError');
  };

  const fetchWithRetry = async (page) => {
    let lastError;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      checkCancelled();
      try {
        metrics.totalCalls++;
        if (attempt > 0) metrics.retriedRequests++;
        return await fetchPage(filterParams, page, pageSize);
      } catch (err) {
        if (err.name === 'AbortError') throw err;
        lastError = err;
        metrics.failedRequests++;
        if (attempt < maxRetries) await backoff(attempt);
      }
    }
    throw lastError;
  };

  const normaliseResponse = (response) => {
    const records =
      response?.records ?? response?.orders ?? response?.data ??
      (Array.isArray(response) ? response : []);
    const total =
      response?.total ?? response?.pagination?.total ??
      (Array.isArray(response) ? response.length : (Array.isArray(records) ? records.length : 0));
    return { records: Array.isArray(records) ? records : [], total: typeof total === 'number' ? total : 0 };
  };

  // ── Step 1: get total ──────────────────────────────────────────────────────
  emit('init', { status: 'Preparing export…' });
  const firstResponse = await fetchWithRetry(1);
  const { records: firstRecords, total } = normaliseResponse(firstResponse);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  metrics.totalPages = totalPages;

  // ── Step 2: workbook + header row ─────────────────────────────────────────
  const wb = XLSXStyle.utils.book_new();

  // Build header row with styled cells
  const wsData = {};
  const range = { s: { r: 0, c: 0 }, e: { r: 0, c: columns.length - 1 } };

  columns.forEach((col, colIdx) => {
    const ref = XLSXStyle.utils.encode_cell({ r: 0, c: colIdx });
    wsData[ref] = { v: col.header, t: 's', s: headerCellStyle() };
  });

  wsData['!ref'] = XLSXStyle.utils.encode_range(range);
  wsData['!freeze'] = { xSplit: 0, ySplit: 1, topLeftCell: 'A2', activePane: 'bottomLeft' };

  XLSXStyle.utils.book_append_sheet(wb, wsData, sheetName);

  // ── Dynamic column width tracker (seeded with header length) ──────────────
  const colMaxLen = columns.map((c) => cellLen(c.header));

  // ── Incremental row appender ──────────────────────────────────────────────
  // dataRowIndex counts rows BELOW the header (0 = first data row)
  // used for alternating band colours.
  let dataRowIndex = 0;

  const appendRecords = (records) => {
    if (!records.length) return;

    records.forEach((record) => {
      const sheetRowIdx = dataRowIndex + 1; // +1 for header offset
      const mapped = mapRow(record);

      columns.forEach((col, colIdx) => {
        const v = mapped[col.key];
        const cell = v == null ? '' : v;

        // Update running max width
        const len = cellLen(cell);
        if (len > colMaxLen[colIdx]) colMaxLen[colIdx] = len;

        const ref = XLSXStyle.utils.encode_cell({ r: sheetRowIdx, c: colIdx });
        const colType = col.type ?? 'text';

        if (colType === 'number' && cell !== '') {
          wsData[ref] = {
            v: typeof cell === 'number' ? cell : Number(cell),
            t: 'n',
            s: numberCellStyle(dataRowIndex),
          };
        } else if (colType === 'date') {
          wsData[ref] = { v: cell, t: 's', s: dateDateStyle(dataRowIndex) };
        } else {
          wsData[ref] = { v: cell, t: 's', s: dataCellStyle(dataRowIndex) };
        }
      });

      dataRowIndex++;
    });

    metrics.totalExported += records.length;

    // Keep !ref in sync with the last written row
    range.e.r = dataRowIndex; // dataRowIndex is now 1 past last written row index in 0-based sheet rows
    wsData['!ref'] = XLSXStyle.utils.encode_range(range);
  };

  // ── Step 3: first page ────────────────────────────────────────────────────
  emit('fetching', { status: 'Fetching data…', currentPage: 1, totalPages, totalRecords: total, exportedCount: 0, percentage: 0 });
  appendRecords(firstRecords);
  emit('writing', { status: 'Writing rows to Excel…', currentPage: 1, totalPages, totalRecords: total, exportedCount: metrics.totalExported, percentage: Math.floor((1 / totalPages) * 100) });
  await yieldToBrowser();

  // ── Step 4: remaining pages ───────────────────────────────────────────────
  for (let page = 2; page <= totalPages; page++) {
    checkCancelled();
    emit('fetching', { status: `Fetching page ${page} of ${totalPages}…`, currentPage: page, totalPages, totalRecords: total, exportedCount: metrics.totalExported, percentage: Math.floor(((page - 1) / totalPages) * 100) });

    const pageResponse = await fetchWithRetry(page);
    const { records: pageRecords } = normaliseResponse(pageResponse);
    if (!pageRecords.length) break;

    appendRecords(pageRecords);
    emit('writing', { status: 'Writing rows to Excel…', currentPage: page, totalPages, totalRecords: total, exportedCount: metrics.totalExported, percentage: Math.floor((page / totalPages) * 100) });
    await yieldToBrowser();
  }

  // ── Step 5: apply dynamic widths + download ───────────────────────────────
  emit('finalizing', { status: 'Finalizing Excel file… Please do not close this tab.', currentPage: totalPages, totalPages, totalRecords: total, exportedCount: metrics.totalExported, percentage: 100 });
  await new Promise((resolve) => setTimeout(resolve, 80));
  checkCancelled();

  wsData['!cols'] = colMaxLen.map((maxLen) => ({
    wch: Math.min(COL_WIDTH_MAX, Math.max(COL_WIDTH_MIN, maxLen + 2)),
  }));

  const timestamp = new Date().toISOString().slice(0, 10);
  XLSXStyle.writeFile(wb, `${filename}_${timestamp}.xlsx`);

  metrics.duration = Date.now() - metrics.startTime;
  emit('done', { status: 'Download started!', currentPage: totalPages, totalPages, totalRecords: total, exportedCount: metrics.totalExported, percentage: 100 });

  return metrics;
}
